import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  Admin,
  CreateAdminRequest,
  UpdateAdminRequest,
  AdminListResponse,
  AdminStatsResponse,
  PasswordChangeRequest,
  AdminActivity,
  AdminRole
} from '../models/admin.interface';

export interface AdminListParams {
  page?: number;
  limit?: number;
  role?: AdminRole;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private readonly API_URL = `${environment.apiUrl}/admin`;
  
  // State management
  private adminsSubject = new BehaviorSubject<Admin[]>([]);
  private statsSubject = new BehaviorSubject<AdminStatsResponse | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  
  public admins$ = this.adminsSubject.asObservable();
  public stats$ = this.statsSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadStats();
  }

  // ================== ADMIN MANAGEMENT ==================

  /**
   * Get list of admins with pagination and filtering
   */
  getAdmins(params: AdminListParams = {}): Observable<AdminListResponse> {
    this.loadingSubject.next(true);
    
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, value.toString());
      }
    });

    return this.http.get<AdminListResponse>(`${this.API_URL}/users`, { params: httpParams })
      .pipe(
        tap(response => {
          if (response.success) {
            this.adminsSubject.next(response.admins);
          }
          this.loadingSubject.next(false);
        })
      );
  }

  /**
   * Get single admin by ID
   */
  getAdmin(adminId: string): Observable<{ success: boolean; admin: Admin }> {
    return this.http.get<{ success: boolean; admin: Admin }>(`${this.API_URL}/users/${adminId}`);
  }

  /**
   * Create new admin
   */
  createAdmin(adminData: CreateAdminRequest): Observable<{ success: boolean; admin: Admin; message: string }> {
    return this.http.post<{ success: boolean; admin: Admin; message: string }>(
      `${this.API_URL}/users/create`,
      adminData
    ).pipe(
      tap(response => {
        if (response.success) {
          this.refreshAdmins();
          this.loadStats();
        }
      })
    );
  }

  /**
   * Update admin
   */
  updateAdmin(adminId: string, updates: UpdateAdminRequest): Observable<{ success: boolean; admin: Admin; message: string }> {
    return this.http.put<{ success: boolean; admin: Admin; message: string }>(
      `${this.API_URL}/users/${adminId}`,
      updates
    ).pipe(
      tap(response => {
        if (response.success) {
          this.refreshAdmins();
          this.loadStats();
        }
      })
    );
  }

  /**
   * Delete admin (deactivate)
   */
  deleteAdmin(adminId: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.API_URL}/users/${adminId}`)
      .pipe(
        tap(response => {
          if (response.success) {
            this.refreshAdmins();
            this.loadStats();
          }
        })
      );
  }

  /**
   * Activate/Deactivate admin
   */
  toggleAdminStatus(adminId: string, isActive: boolean): Observable<{ success: boolean; admin: Admin; message: string }> {
    return this.updateAdmin(adminId, { isActive });
  }

  /**
   * Reset admin password
   */
  resetPassword(adminId: string): Observable<{ success: boolean; tempPassword: string; message: string }> {
    return this.http.post<{ success: boolean; tempPassword: string; message: string }>(
      `${this.API_URL}/users/${adminId}/reset-password`,
      {}
    );
  }

  /**
   * Change admin password
   */
  changePassword(passwordData: PasswordChangeRequest): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.API_URL}/auth/change-password`,
      passwordData
    );
  }

  // ================== STATISTICS ==================

  /**
   * Get admin system statistics
   */
  getStats(): Observable<AdminStatsResponse> {
    return this.http.get<AdminStatsResponse>(`${this.API_URL}/users/stats`)
      .pipe(
        tap(stats => this.statsSubject.next(stats))
      );
  }

  /**
   * Load stats and update state
   */
  loadStats(): void {
    this.getStats().subscribe();
  }

  // ================== ADMIN ACTIVITY ==================

  /**
   * Get admin activity logs
   */
  getAdminActivity(adminId?: string, limit: number = 50): Observable<{ activities: AdminActivity[]; total: number }> {
    let params = new HttpParams().set('limit', limit.toString());
    if (adminId) {
      params = params.set('adminId', adminId);
    }
    
    return this.http.get<{ activities: AdminActivity[]; total: number }>(
      `${this.API_URL}/activity`,
      { params }
    );
  }

  /**
   * Log admin activity
   */
  logActivity(action: string, resource: string, details: any = {}): Observable<any> {
    return this.http.post(`${this.API_URL}/activity/log`, {
      action,
      resource,
      details
    });
  }

  // ================== PERMISSIONS ==================

  /**
   * Get available permissions
   */
  getPermissions(): Observable<{ permissions: string[]; modules: string[] }> {
    return this.http.get<{ permissions: string[]; modules: string[] }>(`${this.API_URL}/permissions`);
  }

  /**
   * Check if current admin has specific permission
   */
  hasPermission(permission: string): Observable<boolean> {
    return this.http.get<{ hasPermission: boolean }>(`${this.API_URL}/permissions/check/${permission}`)
      .pipe(map(response => response.hasPermission));
  }

  // ================== UTILITY METHODS ==================

  /**
   * Refresh admins list
   */
  private refreshAdmins(): void {
    this.getAdmins().subscribe();
  }

  /**
   * Search admins
   */
  searchAdmins(query: string): Observable<AdminListResponse> {
    return this.getAdmins({ search: query, limit: 100 });
  }

  /**
   * Filter admins by role
   */
  getAdminsByRole(role: AdminRole): Observable<AdminListResponse> {
    return this.getAdmins({ role, limit: 100 });
  }

  /**
   * Get current admin count by role
   */
  getCurrentStats(): AdminStatsResponse | null {
    return this.statsSubject.value;
  }

  /**
   * Export admins data
   */
  exportAdmins(format: 'csv' | 'excel' = 'csv'): Observable<Blob> {
    return this.http.get(`${this.API_URL}/users/export`, {
      params: new HttpParams().set('format', format),
      responseType: 'blob'
    });
  }

  // ================== BULK OPERATIONS ==================

  /**
   * Bulk update admins
   */
  bulkUpdateAdmins(adminIds: string[], updates: Partial<UpdateAdminRequest>): Observable<{ success: boolean; updated: number; message: string }> {
    return this.http.put<{ success: boolean; updated: number; message: string }>(
      `${this.API_URL}/users/bulk-update`,
      { adminIds, updates }
    ).pipe(
      tap(response => {
        if (response.success) {
          this.refreshAdmins();
          this.loadStats();
        }
      })
    );
  }

  /**
   * Bulk delete admins
   */
  bulkDeleteAdmins(adminIds: string[]): Observable<{ success: boolean; deleted: number; message: string }> {
    return this.http.delete<{ success: boolean; deleted: number; message: string }>(
      `${this.API_URL}/users/bulk-delete`,
      { body: { adminIds } }
    ).pipe(
      tap(response => {
        if (response.success) {
          this.refreshAdmins();
          this.loadStats();
        }
      })
    );
  }
} 